// The World panel (Kyoto test layout): time and weather overrides, Surprise buttons that start
// any act now (rare ones purple), and the rare things spotted on this phone. The trip's places
// (Niseko, Sapporo, Otaru) follow the live weather (Live · Clear · Snow · Blizzard); other places
// (docs/PLACES.md) list their own weathers. Season, Sounds (picked files or synth only) and Voice
// rows appear when the place has them. (The Level row went with the old Niseko island's unlockables.)
// Explore all shows every island and place in the switcher (off by default; a grown-up's choice is
// kept on the phone, WI-07; a child's Map ignores it).
// A child gets neither Explore all nor the Surprise buttons: both give hidden stages away (N3).
// WI-07 splits it: a child gets the "Look" sheet (pictures, a sticker shelf, one Surprise me);
// grown-ups keep this panel. Surprise runs never count as spotted (Director `surprise`).
import { useMemo, useRef } from 'react'
import { useEvents } from '../data/progress'
import { useSession } from '../data/session'
import { spottedOn } from '../domain/spotted'
import { useModal } from '../app/Sheet'
import { setExplore, useStage } from '../stage/bridge'
import { t } from '../strings/en'
import { usePlacePrefs } from './placePrefs'
import { useWorldOverride } from './worldOverride'

function Seg<T extends number | string | boolean | null>({ label, value, options, onPick }: { label: string; value: T; options: [T, string][]; onPick(v: T): void }) {
  return (
    <div className="wrow">
      <span>{label}</span>
      <div className="seg" role="group" aria-label={label}>
        {options.map(([v, name]) => (
          <button key={name} aria-pressed={value === v} onClick={() => onPick(v)}>
            {name}
          </button>
        ))}
      </div>
    </div>
  )
}

/** This phone's spotted list, with the person's synced spots on this island from their other phones (WI-07). */
function useSpotted(): Record<string, string> {
  const local = useStage((s) => s.spotted)
  const island = useStage((s) => s.island)
  const events = useEvents()
  const me = useSession((s) => s.person?.id)
  return useMemo(() => ({ ...spottedOn(events, me, island), ...local }), [events, me, island, local])
}

/** Big picture buttons for a child (WI-07): an emoji over a short word. */
function Pics<T extends number | string | null>({ label, value, options, onPick }: { label: string; value: T; options: [T, string, string][]; onPick(v: T): void }) {
  return (
    <div className="look-pics" role="group" aria-label={label}>
      {options.map(([v, icon, name]) => (
        <button key={name} className="look-pic press" aria-pressed={value === v} aria-label={name} onClick={() => onPick(v)}>
          <span aria-hidden>{icon}</span>
          <small>{name}</small>
        </button>
      ))}
    </div>
  )
}

/**
 * A child's World sheet, "Look" (WI-07): time and weather as pictures, a shelf of the rare things
 * they've spotted (stickers, the rest still "?"), and one Surprise me that plays a common act.
 * No Explore all and no list of acts (N3). Sounds and voice stay with the grown-up panel.
 */
function LookPanel({ onClose }: { onClose(): void }) {
  const o = useWorldOverride()
  const acts = useStage((s) => s.acts)
  const spotted = useSpotted()
  const place = useStage((s) => s.place)
  const rare = acts.filter((a) => a.rarity !== 'common')
  const common = acts.filter((a) => a.rarity === 'common')
  const l = t.map.look
  const ref = useRef<HTMLDivElement>(null)
  useModal(ref, { onClose, modal: false })
  const found = rare.filter((a) => spotted[a.id]).length
  const surprise = () => {
    const s = useStage.getState()
    const pick = common[Math.floor(Math.random() * common.length)]!
    s.set({ forceAct: { id: pick.id, n: (s.forceAct?.n ?? 0) + 1 } })
    onClose()
  }
  return (
    <div ref={ref} className="panel world-panel look-panel" role="dialog" tabIndex={-1} aria-label={l.title}>
      {/* The fun button first: the panel can scroll on a short phone, and this must never be under it. */}
      {common.length ? (
        <button className="btn y sm press look-surprise" onClick={surprise}>
          {l.surpriseMe}
        </button>
      ) : null}
      <Pics label={t.map.world.time} value={o.tod} onPick={(tod) => o.set({ tod })} options={[[null, '🕒', l.tod.now!], [0, '☀️', l.tod.day!], [1, '🌇', l.tod.dusk!], [2, '🌙', l.tod.night!]]} />
      {!place || place.live ? (
        <Pics label={t.map.world.weather} value={o.wx} onPick={(wx) => o.set({ wx })} options={[[null, '🕒', l.wx.now!], [0, '🌤️', l.wx.clear!], [1, '🌨️', l.wx.snow!], [2, '❄️', l.wx.blizzard!]]} />
      ) : (
        <Pics label={t.map.world.weather} value={place.weather} onPick={(id) => useStage.getState().set({ placeWeather: id })} options={place.weathers.map((x) => [x.id, '🌤️', x.label] as [string, string, string])} />
      )}
      {rare.length ? (
        <div>
          <div className="wtitle">
            {l.shelf} · {l.shelfCount(found, rare.length)}
          </div>
          <ul className="look-shelf">
            {rare.map((a) => (
              <li key={a.id} className={`look-sticker ${a.rarity}${spotted[a.id] ? ' on' : ''}`} aria-label={spotted[a.id] ? a.label : l.hidden}>
                <span aria-hidden>{spotted[a.id] ? '★' : '?'}</span>
                <small>{spotted[a.id] ? a.label : ''}</small>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="look-foot">
        <button className="btn w sm press" onClick={onClose}>
          {t.map.world.done}
        </button>
      </div>
    </div>
  )
}

export function WorldPanel({ child, onClose }: { child: boolean; onClose(): void }) {
  if (child) return <LookPanel onClose={onClose} />
  return <GrownUpWorld onClose={onClose} />
}

/** The grown-up's World panel: every override, Explore all and a Surprise for every act. */
function GrownUpWorld({ onClose }: { onClose(): void }) {
  const o = useWorldOverride()
  const acts = useStage((s) => s.acts)
  const spotted = useSpotted()
  const rare = acts.filter((a) => a.rarity !== 'common')
  const place = useStage((s) => s.place)
  const explore = useStage((s) => s.explore)
  const prefs = usePlacePrefs()
  const w = t.map.world
  // Not modal: the stage behind stays live while the time and weather change (WI-28).
  const ref = useRef<HTMLDivElement>(null)
  useModal(ref, { onClose, modal: false })
  return (
    <div ref={ref} className="panel world-panel" role="dialog" tabIndex={-1} aria-label={w.title}>
      <Seg label={w.time} value={o.tod} onPick={(tod) => o.set({ tod })} options={[[null, w.live], [0, 'Day'], [1, 'Dusk'], [2, 'Night']]} />
      {!place || place.live ? (
        <Seg label={w.weather} value={o.wx} onPick={(wx) => o.set({ wx })} options={[[null, w.live], [0, 'Clear'], [1, 'Snow'], [2, 'Blizzard']]} />
      ) : null}
      {place ? (
        <>
          {place.seasons ? (
            <Seg label={w.season} value={place.season} onPick={(id) => useStage.getState().set({ placeSeason: id })} options={place.seasons.map((x) => [x.id, x.label] as [string, string])} />
          ) : null}
          {place.live ? null : (
            <Seg label={w.weather} value={place.weather} onPick={(id) => useStage.getState().set({ placeWeather: id })} options={place.weathers.map((x) => [x.id, x.label] as [string, string])} />
          )}
          {place.sounds ? <Seg label={w.sounds} value={prefs.sfx} onPick={(sfx) => prefs.set({ sfx })} options={[['mixed', w.mixed], ['synth', w.synthOnly]]} /> : null}
          {place.voices ? (
            <Seg
              label={w.voice}
              value={place.voices.speaker}
              onPick={(voice) => prefs.set({ voice })}
              options={[['off', w.off] as [string, string], ...place.voices.speakers.map((x) => [x.id, x.label] as [string, string])]}
            />
          ) : null}
        </>
      ) : null}
      <Seg label={w.explore} value={explore} onPick={setExplore} options={[[false, w.off], [true, w.on]]} />
      <div>
          <div className="wtitle">{t.map.world.surprise}</div>
          <div className="acts">
            {acts.map((a) => (
              <button
                key={a.id}
                className={`act-btn press ${a.rarity}`}
                onClick={() => {
                  const s = useStage.getState()
                  s.set({ forceAct: { id: a.id, n: (s.forceAct?.n ?? 0) + 1 } })
                  onClose()
                }}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>
      {rare.length ? (
        <div>
          <div className="wtitle">{t.map.world.spotted}</div>
          <ul className="spotted">
            {rare.map((a) => (
              <li key={a.id}>
                <span>{spotted[a.id] ? a.label : t.map.world.notYet}</span>
                <span>{spotted[a.id] ? new Date(spotted[a.id]!).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '?'}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <p className="caption">{w.note}</p>
      {explore ? <p className="caption">{w.exploreNote}</p> : null}
      {/* The voice credit lives in Settings › Credits now (WI-30). */}
      <button className="btn w sm press" onClick={onClose}>
        {t.map.world.done}
      </button>
    </div>
  )
}
